import React, { useState, useEffect } from 'react';
import { Offcanvas, Row, Col, Button, Card, Form, Modal, InputGroup, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useCarrinho } from '../services/CarrinhoContext';
import { criarPagamento, validarVoucher, criarComanda, adicionarProduto, getProdutoUpsells } from '../services/api';
import { formatPrice } from '../utils/currency';
import { isNewUser as checkIsNewUser, getUserFromToken } from '../utils/jwt';
import { getCardBrandLogo, getCardBrandName } from '../utils/cardBrand';
import EnderecoSelectorModal from './EnderecoSelectorModal';
import LoginModal from './LoginModal';
import UserDataModal from './UserDataModal';
import CartoesSalvosModal from './CartoesSalvosModal';
import UpselModal from './UpselModal';
import Lottie from 'lottie-react';
import { ArrowLeft, Check, Bag, Truck, Cash, CheckCircle, LogIn, UserPlus, CardShield, CreditCard, Copy, BadgeCheck, Alarm, Barcode, User } from 'iconoir-react';
import deliveryEmptyAnimation from '../assets/delivery-empty.json';
import pedidoEnviadoAnimation from '../assets/pedido-enviado-cardapio.json';
import pedidoEnviadoCozinhaAnimation from '../assets/pedido-enviado-cozinha-app.json';
import cartaoCreditoAnimation from '../assets/cartao-credito.json';
import posCartaoAnimation from '../assets/POS-cartao.json';
import segurancaAnimation from '../assets/seguranca.json';
import cashbackAnimation from '../assets/cashback-grub.json';
import metaAtingidaAnimation from '../assets/meta-atingida.json';

import './CarrinhoOffcanvas.css';
import './CarrinhoOffcanvas-button-fix.css';

function CarrinhoOffcanvas({ show, onHide, onOpenMeusPedidos }) {
  const {
    itens,
    total,
    tipoEntrega,
    endereco,
    mesa,
    nomeCliente,
    mesaCliente,
    comandaExistente,
    ultimaComanda,
    upselsApresentados,
    removerItem,
    setEndereco,
    setMesa,
    setNomeCliente,
    setMesaCliente,
    setComanda,
    setUltimaComanda,
    limparCarrinho,
    atualizarQuantidade,
    addUpselApresentado,
    limparCarrinhoStorage,
    brand
  } = useCarrinho();
  
  const [showEnderecoModal, setShowEnderecoModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const taxaServico = true; // Taxa de serviço sempre aplicada
  const [voucher, setVoucher] = useState('');
  const [descontoVoucher, setDescontoVoucher] = useState(0);
  const [erroVoucher, setErroVoucher] = useState('');
  const [erroPagamento, setErroPagamento] = useState('');
  const taxaEntrega = tipoEntrega === 'delivery' && endereco?.entrega?.valorEntrega ? parseFloat(endereco.entrega.valorEntrega) : 0;
  const valorTaxaServico = total * 0.1;
  const [viewMode, setViewMode] = useState('carrinho'); // 'carrinho', 'pagamento', 'pix', 'processando', 'sucesso'
  const [tipoPagamento, setTipoPagamento] = useState('');
  const [chavePIX, setChavePIX] = useState('');
  const [pixTimer, setPixTimer] = useState(360); // 6 minutos em segundos
  const [cardForm, setCardForm] = useState({
    number: '',
    name: '',
    cvc: '',
    expiry: '',
    cpf: ''
  });
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [usuario, setUsuario] = useState(null);
  const [showUserDataModal, setShowUserDataModal] = useState(false);
  const [loadingUserData, setLoadingUserData] = useState(false);
  const [showCartoesSalvosModal, setShowCartoesSalvosModal] = useState(false);
  const [cartaoSelecionado, setCartaoSelecionado] = useState(null);
  const [usarNovoCartao, setUsarNovoCartao] = useState(false);
  const [showBalcaoModal, setShowBalcaoModal] = useState(false);
  const [showUpselModal, setShowUpselModal] = useState(false);
  const [upselProducts, setUpselProducts] = useState([]);
  const [loadingUpsels, setLoadingUpsels] = useState(false);

  const [showCopiadoAnimation, setShowCopiadoAnimation] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Reset viewMode quando o offcanvas for fechado
  useEffect(() => {
    if (!show) {
      setViewMode('carrinho');
      setTipoPagamento('');
      setChavePIX('');
      setPixTimer(360);
      setUsarNovoCartao(false);
      setShowUpselModal(false);
      setUpselProducts([]);
      // Limpar flag de dados atualizados quando sair do carrinho
      localStorage.removeItem('sitri_user_data_updated');
    }
  }, [show]);

  // Limpar mesa quando tipo de entrega mudar para delivery ou balcão
  useEffect(() => {
    if ((tipoEntrega === 'delivery' || tipoEntrega === 'balcao') && mesa) {
      setMesa(null);
    }
  }, [tipoEntrega]); // Removido setMesa das dependências

  // Timer do PIX
  useEffect(() => {
    let interval;
    if (viewMode === 'pix' && pixTimer > 0) {
      interval = setInterval(() => {
        setPixTimer(prev => {
          if (prev <= 1) {
            // Apenas voltar para pagamento se não houver chave PIX ativa
            if (!chavePIX) {
              setViewMode('pagamento');
            }
            return 360;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [viewMode, pixTimer]);

  // Polling para verificar status do pagamento PIX
  useEffect(() => {
    let pollInterval;
    if (viewMode === 'pix' && ultimaComanda?.id && chavePIX) {
      const verificarPagamento = async () => {
        try {
          const { verificarStatusPagamento } = await import('../services/api');
          const resultado = await verificarStatusPagamento(brand, ultimaComanda.id);
          
          if (resultado && resultado.success && resultado.pago) {
            // Parar polling e timer
            if (pollInterval) clearInterval(pollInterval);
            
            limparCarrinho();
            limparCarrinhoStorage();
            setVoucher('');
            setDescontoVoucher(0);
            setErroVoucher('');
            setChavePIX('');
            setPixTimer(360);
            setViewMode('sucesso');
          }
        } catch (error) {
          // Erro silencioso - continua tentando
        }
      };
      
      pollInterval = setInterval(verificarPagamento, 3000);
      verificarPagamento();
    }
    
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [viewMode, ultimaComanda?.id, chavePIX, brand]);

  // Verificar se precisa de login quando carrinho for aberto no delivery
  useEffect(() => {
    if (show && tipoEntrega === 'delivery') {
      const token = localStorage.getItem('sitri_token');
      if (!token || !usuario) {
        setShowLoginModal(true);
      } else {
        // Se está logado e não tem endereço, carregar primeiro endereço automaticamente
        if (!endereco) {
          carregarPrimeiroEndereco();
        }
      }
    }
  }, [show, tipoEntrega, usuario, endereco]);

  // Verificar upsells quando carrinho for aberto
  useEffect(() => {
    if (show && itens.length > 0 && viewMode === 'carrinho') {
      verificarUpsells();
    }
  }, [show, itens, viewMode]);

  // Função para carregar automaticamente o primeiro endereço
  const carregarPrimeiroEndereco = async () => {
    try {
      const token = localStorage.getItem('sitri_token');
      if (!token) return;

      const { getClienteProfile, validarEnderecoEntrega } = await import('../services/api');
      const resultado = await getClienteProfile(token);
      
      if (resultado && resultado.success && resultado.enderecos && resultado.enderecos.length > 0) {
        const primeiroEndereco = resultado.enderecos[0];
        const enderecoFormatado = `${primeiroEndereco.rua}, ${primeiroEndereco.numero} - ${primeiroEndereco.bairro}, ${primeiroEndereco.cidade}`;
        
        // Validar endereço para obter taxa de entrega
        const validacao = await validarEnderecoEntrega(
          brand,
          enderecoFormatado,
          primeiroEndereco.cep,
          primeiroEndereco.lat,
          primeiroEndereco.lng
        );
        
        if (validacao && validacao.success && validacao.entrega && validacao.entrega.podeEntregar) {
          // Preencher dados do usuário para entrega
          const user = localStorage.getItem('sitri_user');
          let nomeEntrega = '';
          let telefoneEntrega = '';
          
          if (user) {
            try {
              const userData = JSON.parse(user);
              nomeEntrega = userData.nome || '';
              telefoneEntrega = userData.telefone || '';
            } catch (error) {
              // Erro silencioso
            }
          }
          
          const enderecoCompleto = {
            endereco: enderecoFormatado,
            lat: primeiroEndereco.lat,
            lng: primeiroEndereco.lng,
            place_id: primeiroEndereco.place_id,
            complemento: primeiroEndereco.complemento || '',
            entrega: {
              ...validacao.entrega,
              nome: nomeEntrega || null,
              telefone: telefoneEntrega || null
            }
          };
          
          setEndereco(enderecoCompleto);
        }
      }
    } catch (error) {
      // Erro silencioso - não atrapalha a experiência do usuário
    }
  };

  // Verificar se usuário está logado e preencher dados automaticamente
  useEffect(() => {
    const token = localStorage.getItem('sitri_token');
    const user = localStorage.getItem('sitri_user');
    
    if (token && user) {
      try {
        const userData = JSON.parse(user);
        setUsuario(userData);
        
        // Preencher dados automaticamente se disponíveis
        if (userData.cpf && !userData.cpf.startsWith('999')) {
          setCardForm(prev => ({
            ...prev,
            cpf: userData.cpf,
            name: userData.nome ? userData.nome.toUpperCase() : prev.name
          }));
        }
      } catch (error) {
        // Erro silencioso
      }
    }
    
    // Também verificar JWT diretamente se não tiver dados do localStorage
    if (token && !user) {
      const userData = getUserFromToken(token);
      if (userData && userData.cpf && !userData.cpf.startsWith('999')) {
        setCardForm(prev => ({
          ...prev,
          cpf: userData.cpf,
          name: userData.nome ? userData.nome.toUpperCase() : prev.name
        }));
      }
    }
  }, []);
  
  // Preencher dados quando entrar na tela de pagamento
  useEffect(() => {
    if (viewMode === 'pagamento' && usuario) {
      // Preencher dados do usuário logado nos formulários
      if (usuario.cpf && !usuario.cpf.startsWith('999')) {
        setCardForm(prev => ({
          ...prev,
          cpf: usuario.cpf,
          name: usuario.nome ? usuario.nome.toUpperCase() : prev.name
        }));
      }
    }
  }, [viewMode, usuario]);

  // Carregar primeiro cartão automaticamente quando entrar na tela de pagamento (exceto balcão)
  useEffect(() => {
    if (viewMode === 'pagamento' && tipoPagamento === 'CREDITO' && usuario && !cartaoSelecionado && !usarNovoCartao && tipoEntrega !== 'balcao') {
      carregarPrimeiroCartao();
    }
  }, [viewMode, tipoPagamento, usuario, cartaoSelecionado, usarNovoCartao, tipoEntrega]);

  // Função para carregar automaticamente o primeiro cartão
  const carregarPrimeiroCartao = async () => {
    try {
      const token = localStorage.getItem('sitri_token');
      if (!token) {
        return;
      }

      const { getCartoes } = await import('../services/api');
      const resultado = await getCartoes(token);
      
      if (resultado && resultado.success && resultado.data && resultado.data.length > 0) {
        const cartaoRaw = resultado.data[0];
        // Formatar cartão para o formato esperado
        const primeiroCartao = {
          id: cartaoRaw.id,
          numero: cartaoRaw.extra_info?.creditCardNumber || '****',
          bandeira: cartaoRaw.extra_info?.creditCardBrand || 'UNKNOWN',
          token: cartaoRaw.id
        };
        setCartaoSelecionado(primeiroCartao);
        
        // Limpar formulário de cartão novo já que um cartão salvo foi selecionado
        setCardForm(prev => ({
          number: '',
          name: '',
          cvc: '',
          expiry: '',
          cpf: prev.cpf // Manter CPF
        }));
      } else {
      }
    } catch (error) {
    }
  };

  const alterarQuantidade = (index, novaQuantidade) => {
    if (novaQuantidade <= 0) {
      removerItem(index);
    } else {
      atualizarQuantidade(index, novaQuantidade);
    }
  };

  const aplicarVoucher = async () => {
    if (!voucher.trim()) {
      setErroVoucher('Digite um código de voucher');
      return;
    }

    setLoading(true);
    setErroVoucher('');
    try {
      const resultado = await validarVoucher(brand, voucher, total);
      
      if (resultado && resultado.success) {
        setDescontoVoucher(resultado.voucher.valorDesconto);
        setErroVoucher('');
      } else {
        setDescontoVoucher(0);
        setErroVoucher(resultado?.message || 'Voucher inválido');
      }
    } catch (error) {
      setDescontoVoucher(0);
      setErroVoucher('Erro ao validar voucher');
    } finally {
      setLoading(false);
    }
  };

  const calcularTotal = () => {
    let totalFinal = total;
    if (taxaServico && tipoEntrega !== 'delivery') totalFinal += valorTaxaServico;
    totalFinal += taxaEntrega;
    totalFinal -= descontoVoucher;
    return Math.max(0, totalFinal);
  };

  // Verificar se há upsells para produtos no carrinho
  const verificarUpsells = async () => {
    try {
      setLoadingUpsels(true);
      const upsellsEncontrados = [];
      
      // Verificar upsells para cada produto no carrinho
      for (const item of itens) {
        const resultado = await getProdutoUpsells(brand, item.produto.id);
        if (resultado && resultado.success && resultado.data && resultado.data.length > 0) {
          upsellsEncontrados.push(...resultado.data);
        }
      }
      
      // Remover duplicatas baseado no ID do produto
      const upsellsUnicos = upsellsEncontrados.filter((produto, index, self) => 
        index === self.findIndex(p => p.id === produto.id)
      );
      
      // Filtrar produtos que já estão no carrinho e upsells já apresentados
      const produtosNoCarrinho = itens.map(item => item.produto.id);
      const upsellsFiltrados = upsellsUnicos.filter(produto => 
        !produtosNoCarrinho.includes(produto.id) && !upselsApresentados.includes(produto.id)
      );
      
      if (upsellsFiltrados.length > 0) {
        // Marcar todos os upsells como apresentados
        upsellsFiltrados.forEach(produto => addUpselApresentado(produto.id));
        setUpselProducts(upsellsFiltrados);
        setShowUpselModal(true);
      }
    } catch (error) {
      // Erro silencioso - não atrapalha a experiência
    } finally {
      setLoadingUpsels(false);
    }
  };

  const pagarAgora = () => {
    // Verificar se é usuário temporário antes de prosseguir
    const token = localStorage.getItem('sitri_token');
    const forceUserData = localStorage.getItem('sitri_force_user_data');
    const savedUser = localStorage.getItem('sitri_user');
    const userDataUpdated = localStorage.getItem('sitri_user_data_updated');
    
    let needsUserData = false;
    
    // Se dados já foram atualizados, não pedir novamente
    if (userDataUpdated === 'true') {
      needsUserData = false;
    }
    // Verificar se precisa de dados do usuário
    else if (forceUserData === 'true') {
      needsUserData = true;
    }
    // Verificar se usuário tem dados válidos salvos
    else if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        // Se tem nome, email e CPF válido (não temporário), não precisa de dados
        if (userData.nome && userData.email && userData.cpf && !userData.cpf.startsWith('999')) {
          needsUserData = false;
        } else if (token && checkIsNewUser(token)) {
          needsUserData = true;
        }
      } catch (error) {
        // Se erro ao parsear, verificar token
        if (token && checkIsNewUser(token)) {
          needsUserData = true;
        }
      }
    } else if (token && checkIsNewUser(token)) {
      needsUserData = true;
    }
    
    if (needsUserData) {
      setShowUserDataModal(true);
    } else {
      setViewMode('pagamento');
    }
  };

  const podeFinalizarPedido = () => {
    if (itens.length === 0) return false;
    if (tipoEntrega === 'delivery') {
      if (!endereco) return false;
      // Verificar se o endereço pode receber entrega
      if (endereco.entrega && !endereco.entrega.podeEntregar) return false;
    }
    return true;
  };

  const voltarParaCarrinho = () => {
    setViewMode('carrinho');
  };

  const handleCardChange = (field, value) => {
    setCardForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatCardNumber = (value) => {
    return value.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();
  };

  const formatExpiry = (value) => {
    return value.replace(/\D/g, '').replace(/(\d{2})(\d)/, '$1/$2');
  };

  const formatCPF = (value) => {
    return value.replace(/\D/g, '').replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const formatGroup = (arr, showValue = false) => {
    if (!Array.isArray(arr) || arr.length === 0) return '';
    const map = {};
    for (const a of arr) {
      const nome = a?.nome || '';
      const valor = parseFloat(a?.valor || 0) || 0;
      if (!map[nome]) map[nome] = { qtd: 0, valor };
      map[nome].qtd += 1;
    }
    const parts = Object.entries(map).map(([nome, info]) => {
      const mult = info.qtd > 1 ? ` x${info.qtd}` : '';
      const extra = showValue && info.valor ? ` (+${formatPrice(info.valor * info.qtd)})` : '';
      return `${nome}${mult}${extra}`;
    });
    return parts.join(', ');
  };

  const gerarPIX = async () => {
    try {
      setLoading(true);
      setLoadingProgress(0);
      setErroPagamento('');
      
      const progressInterval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 100) {
            clearInterval(progressInterval);
            return 100;
          }
          return prev + 4;
        });
      }, 100);
      
      // Validar endereço para delivery
      if (tipoEntrega === 'delivery') {
        if (!endereco) {
          setErroPagamento('Selecione um endereço para delivery');
          return;
        }
        if (endereco.entrega && !endereco.entrega.podeEntregar) {
          setErroPagamento('O endereço selecionado não está na área de entrega. Selecione outro endereço.');
          return;
        }
      }
      
      // Gerar descrição do PIX
      const nomeLoja = window.lojaInfo?.nome || brand;
      const primeiroProduto = itens.length > 0 ? itens[0].produto.nome : 'pedido';
      const descricaoPix = itens.length === 1 
        ? `Pagamento de ${primeiroProduto} feito em ${nomeLoja} com GRUB DIGITAL`
        : `Pagamento de ${primeiroProduto} e mais ${itens.length - 1} ${itens.length === 2 ? 'item' : 'itens'} feito em ${nomeLoja} com GRUB DIGITAL`;

      const dadosPagamento = {
        comanda_id: comandaExistente?.id || null, // Usar comanda existente se houver
        tipo_entrega: tipoEntrega,
        endereco: tipoEntrega === 'delivery' ? endereco : null,
        mesa: tipoEntrega === 'local' ? (mesa || null) : null, // Só enviar mesa se for tipo local
        nome_cliente: nomeCliente || null,
        mesa_cliente: mesaCliente || null,
        tipo_pagamento: 'PIX',
        dados_cartao: {
          name: cardForm.name,
          cpf: cardForm.cpf
        },
        descricao_pix: descricaoPix,
        voucher_codigo: voucher || null,
        voucher_desconto: descontoVoucher,
        taxa_servico: taxaServico && tipoEntrega !== 'delivery',
        taxa_entrega: taxaEntrega,
        status: 'producao',
        itens: itens.map(item => {
          const adicionais = item.adicionais.filter(adicional => adicional.b_adicional === 1);
          const opcionais = item.adicionais.filter(adicional => adicional.b_adicional === 0);
          
          return {
            produto_id: item.produto.id,
            quantidade: item.quantidade,
            valor_unitario: item.valorTotal,
            valor_total: item.valorTotal * item.quantidade,
            variacoes: item.variacoes,
            adicionais: adicionais,
            opcionais: [...opcionais, ...item.opcionais],
            observacoes: item.complemento || '',
            status: 'producao'
          };
        }),
        valor_total: calcularTotal()
      };

      const resultado = await criarPagamento(brand, dadosPagamento);
      
      clearInterval(progressInterval);
      setLoadingProgress(100);
      
      if (resultado && resultado.success) {
        // Atualizar estados em lote para evitar re-renderizações múltiplas
        const comanda = resultado.comanda;
        const pixCode = resultado.pagamento.pix_code;
        
        // Salvar no localStorage primeiro
        localStorage.setItem('sitri_ultima_comanda', JSON.stringify(comanda));
        localStorage.setItem('sitri_pix_code', pixCode);
        
        // Marcar que usuário completou um pagamento (não pedir dados novamente)
        localStorage.setItem('sitri_user_data_updated', 'true');
        
        // Atualizar estados
        setComanda(comanda);
        setUltimaComanda(comanda);
        setChavePIX(pixCode);
        setPixTimer(360); // Reset timer para 6 minutos
        
        // Mostrar tela do PIX
        setViewMode('pix');
      } else {
        const errorMessage = resultado?.message || 'Erro ao gerar PIX';
        if (errorMessage.includes('CPF') || errorMessage.includes('cliente') || errorMessage.includes('dados')) {
          setErroPagamento('Por favor, verifique os dados e tente novamente.');
        } else {
          setErroPagamento(errorMessage);
        }
      }
      
    } catch (error) {
      setErroPagamento('Erro ao processar pagamento. Verifique os dados e tente novamente.');
    } finally {
      setLoading(false);
      setLoadingProgress(0);
    }
  };

  const pagarComCartaoSalvo = async () => {
    if (!cartaoSelecionado) {
      setErroPagamento('Selecione um cartão');
      return;
    }
    
    // Validar endereço para delivery
    if (tipoEntrega === 'delivery') {
      if (!endereco) {
        setErroPagamento('Selecione um endereço para delivery');
        return;
      }
      if (endereco.entrega && !endereco.entrega.podeEntregar) {
        setErroPagamento('O endereço selecionado não está na área de entrega. Selecione outro endereço.');
        return;
      }
    }

    setViewMode('processando');
    setErroPagamento('');

    try {
      const dadosPagamento = {
        comanda_id: comandaExistente?.id || null,
        tipo_entrega: tipoEntrega,
        endereco: tipoEntrega === 'delivery' ? endereco : null,
        mesa: tipoEntrega === 'local' ? (mesa || null) : null,
        nome_cliente: nomeCliente || null,
        mesa_cliente: mesaCliente || null,
        tipo_pagamento: 'CREDITO',
        cartao_salvo: {
          id: cartaoSelecionado.id,
          token: cartaoSelecionado.token
        },
        voucher_codigo: voucher || null,
        voucher_desconto: descontoVoucher,
        taxa_servico: taxaServico && tipoEntrega !== 'delivery',
        taxa_entrega: taxaEntrega,
        status: 'producao',
        itens: itens.map(item => ({
          produto_id: item.produto.id,
          quantidade: item.quantidade,
          valor_unitario: item.valorTotal,
          valor_total: item.valorTotal * item.quantidade,
          variacoes: item.variacoes,
          adicionais: item.adicionais,
          opcionais: item.opcionais,
          observacoes: item.complemento || '',
          status: 'producao'
        })),
        valor_total: calcularTotal()
      };

      const resultado = await criarPagamento(brand, dadosPagamento);
      
      if (resultado && resultado.success) {
        const comanda = resultado.comanda;
        
        localStorage.setItem('sitri_ultima_comanda', JSON.stringify(comanda));
        localStorage.setItem('sitri_user_data_updated', 'true');
        
        setComanda(comanda);
        setUltimaComanda(comanda);
        
        limparCarrinho();
        limparCarrinhoStorage();
        localStorage.removeItem('sitri_ultima_comanda');
        setVoucher('');
        setDescontoVoucher(0);
        setErroVoucher('');
        
        setTimeout(() => {
          setViewMode('sucesso');
        }, 3000);
      } else {
        const errorMessage = resultado?.message || 'Erro ao processar pagamento';
        if (errorMessage.includes('CPF') || errorMessage.includes('cliente') || errorMessage.includes('dados')) {
          setErroPagamento('Por favor, verifique os dados e tente novamente.');
        } else {
          setErroPagamento(errorMessage);
        }
        setViewMode('pagamento');
      }

    } catch (error) {
      setErroPagamento('Erro ao processar pagamento. Verifique os dados e tente novamente.');
      setViewMode('pagamento');
    }
  };

  const pagarComCartao = async () => {
    if (!cardForm.number || !cardForm.name || !cardForm.cvc || !cardForm.expiry) {
      setErroPagamento('Preencha todos os campos do cartão');
      return;
    }
    
    // Validar endereço para delivery
    if (tipoEntrega === 'delivery') {
      if (!endereco) {
        setErroPagamento('Selecione um endereço para delivery');
        return;
      }
      if (endereco.entrega && !endereco.entrega.podeEntregar) {
        setErroPagamento('O endereço selecionado não está na área de entrega. Selecione outro endereço.');
        return;
      }
    }

    setViewMode('processando');
    setErroPagamento('');

    try {
      const dadosPagamento = {
        comanda_id: comandaExistente?.id || null,
        tipo_entrega: tipoEntrega,
        endereco: tipoEntrega === 'delivery' ? endereco : null,
        mesa: tipoEntrega === 'local' ? (mesa || null) : null,
        nome_cliente: nomeCliente || null,
        mesa_cliente: mesaCliente || null,
        tipo_pagamento: 'CREDITO',
        dados_cartao: {
          ...cardForm,
          salvar_cartao: tipoEntrega !== 'balcao'
        },
        voucher_codigo: voucher || null,
        voucher_desconto: descontoVoucher,
        taxa_servico: taxaServico && tipoEntrega !== 'delivery',
        taxa_entrega: taxaEntrega,
        status: 'producao',
        itens: itens.map(item => ({
          produto_id: item.produto.id,
          quantidade: item.quantidade,
          valor_unitario: item.valorTotal,
          valor_total: item.valorTotal * item.quantidade,
          variacoes: item.variacoes,
          adicionais: item.adicionais,
          opcionais: item.opcionais,
          observacoes: item.complemento || '',
          status: 'producao'
        })),
        valor_total: calcularTotal()
      };

      const resultado = await criarPagamento(brand, dadosPagamento);
      
      if (resultado && resultado.success) {
        const comanda = resultado.comanda;
        
        localStorage.setItem('sitri_ultima_comanda', JSON.stringify(comanda));
        localStorage.setItem('sitri_user_data_updated', 'true');
        
        setComanda(comanda);
        setUltimaComanda(comanda);
        
        limparCarrinho();
        limparCarrinhoStorage();
        localStorage.removeItem('sitri_ultima_comanda');
        setVoucher('');
        setDescontoVoucher(0);
        setErroVoucher('');
        
        setTimeout(() => {
          setViewMode('sucesso');
        }, 3000);
      } else {
        const errorMessage = resultado?.message || 'Erro ao processar pagamento';
        if (errorMessage.includes('CPF') || errorMessage.includes('cliente') || errorMessage.includes('dados')) {
          setErroPagamento('Por favor, verifique os dados e tente novamente.');
        } else {
          setErroPagamento(errorMessage);
        }
        setViewMode('pagamento');
      }

    } catch (error) {
      setErroPagamento('Erro ao processar pagamento. Verifique os dados e tente novamente.');
      setViewMode('pagamento');
    }
  };

  const copiarPIX = async () => {
    if (!chavePIX) {
      alert('Código PIX não disponível');
      return;
    }
    
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(chavePIX);
      } else {
        // Fallback para navegadores antigos
        const textArea = document.createElement('textarea');
        textArea.value = chavePIX;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      
      setShowCopiadoAnimation(true);
      setTimeout(() => {
        setShowCopiadoAnimation(false);
      }, 2000);
    } catch (error) {
      console.error('Erro ao copiar:', error);
      alert('Não foi possível copiar automaticamente. Por favor, copie o código manualmente.');
    }
  };



  const handleSaveUserData = async (userData) => {
    try {
      setLoadingUserData(true);
      
      const token = localStorage.getItem('sitri_token');
      if (!token) {
        alert('Sessão expirada. Faça login novamente.');
        return;
      }

      const { updateTempUser } = await import('../services/api');
      const data = await updateTempUser(token, userData);
      
      if (data && data.success) {
        // Atualizar dados do usuário no localStorage
        const currentUser = JSON.parse(localStorage.getItem('sitri_user') || '{}');
        const updatedUser = {
          ...currentUser,
          nome: userData.nome,
          email: userData.email,
          cpf: userData.cpf,
          newUser: false
        };
        
        localStorage.setItem('sitri_user', JSON.stringify(updatedUser));
        localStorage.removeItem('sitri_force_user_data');
        
        // Marcar que dados foram atualizados para evitar nova solicitação
        localStorage.setItem('sitri_user_data_updated', 'true');
        
        // Atualizar estado do usuário e preencher dados no formulário
        setUsuario(updatedUser);
        setCardForm(prev => ({
          ...prev,
          cpf: userData.cpf,
          name: userData.nome.toUpperCase()
        }));
        
        setShowUserDataModal(false);
        
        // Continuar para pagamento
        setViewMode('pagamento');
      } else {
        alert(data?.message || 'Erro ao salvar dados. Tente novamente.');
      }
    } catch (error) {
      alert('Erro ao salvar dados. Tente novamente.');
    } finally {
      setLoadingUserData(false);
    }
  };
  
  const handleLoginSuccess = (userData) => {
    setUsuario(userData);
    localStorage.setItem('sitri_user', JSON.stringify(userData));
    
    // Preencher dados automaticamente após login
    if (userData.cpf && !userData.cpf.startsWith('999')) {
      setCardForm(prev => ({
        ...prev,
        cpf: userData.cpf,
        name: userData.nome ? userData.nome.toUpperCase() : prev.name
      }));
    }
    
    setShowLoginModal(false);
  };

  const pagarComGarcom = async () => {
    setLoading(true);
    setLoadingProgress(0);
    
    // Simular progresso de loading em 2.5 segundos
    const progressInterval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 4; // 100% / 25 steps = 4% por step (2500ms / 100ms)
      });
    }, 100);
    
    setTipoPagamento('GARCOM'); // Marcar que é pagamento com garçom

    try {
      // Para balcão, SEMPRE criar nova comanda (não reutilizar comanda de mesa)
      if (tipoEntrega === 'balcao') {
        const dadosComanda = {
          tipo_entrega: 'balcao',
          b_delivery: 0,
          b_takeaway: 1,
          b_balcao: 1,
          endereco: null,
          mesa: null, // Balcão nunca tem mesa
          nome_cliente: nomeCliente || 'Cliente Balcão',
          mesa_cliente: mesaCliente || null,
          status: 'aberta',
          pago: 0,
          tipo_pagamento: null,
          itens: itens.map(item => {
            const adicionaisFormatados = item.adicionais.filter(adicional => adicional.b_adicional === 1);
            const opcionaisFormatados = [...item.adicionais.filter(adicional => adicional.b_adicional === 0), ...item.opcionais];
            
            return {
              produto_id: item.produto.id,
              quantidade: item.quantidade,
              valor_unitario: item.valorTotal,
              valor_total: item.valorTotal * item.quantidade,
              variacoes: item.variacoes,
              adicionais: adicionaisFormatados,
              opcionais: opcionaisFormatados,
              observacoes: item.complemento || '',
              status: 'producao'
            };
          }),
          valor_total: calcularTotal()
        };

        const comanda = await criarComanda(brand, dadosComanda);
        if (comanda && comanda.id) {
          localStorage.setItem('sitri_ultima_comanda', JSON.stringify(comanda));
          localStorage.setItem('sitri_user_data_updated', 'true');
          
          setComanda(comanda);
          setUltimaComanda(comanda);
        } else {
          throw new Error('Falha ao criar comanda');
        }
      } else {
        // Para local (mesa), verificar se existe comanda aberta
        let comandaParaVerificar = ultimaComanda || comandaExistente;
        
        // Se tem comanda, buscar status atualizado do backend antes de usar
        if (comandaParaVerificar?.id) {
          try {
            const { getComanda } = await import('../services/api');
            const comandaAtualizada = await getComanda(brand, comandaParaVerificar.id);
            if (comandaAtualizada) {
              comandaParaVerificar = comandaAtualizada;
              // Atualizar localStorage com dados atualizados
              localStorage.setItem('sitri_ultima_comanda', JSON.stringify(comandaAtualizada));
            }
          } catch (error) {
            console.log('[CarrinhoOffcanvas] Erro ao buscar comanda atualizada:', error);
          }
        }
        
        console.log('[CarrinhoOffcanvas] Verificando comanda:', {
          id: comandaParaVerificar?.id,
          status: comandaParaVerificar?.status,
          pago: comandaParaVerificar?.pago
        });
        
        const comandaAberta = comandaParaVerificar && 
                              comandaParaVerificar.status !== 'fechada' && 
                              comandaParaVerificar.status !== 'cancelada' && 
                              !comandaParaVerificar.pago 
                              ? comandaParaVerificar 
                              : null;
        
        // Se comanda está fechada, limpar localStorage
        if (!comandaAberta && comandaParaVerificar) {
          console.log('[CarrinhoOffcanvas] Comanda fechada/paga, limpando localStorage');
          localStorage.removeItem('sitri_ultima_comanda');
          setUltimaComanda(null);
        }
        
        console.log('[CarrinhoOffcanvas] Comanda aberta?', comandaAberta ? 'SIM' : 'NÃO');
        
        if (comandaAberta && comandaAberta.id) {
          // Adicionar produtos à comanda existente aberta
          try {
            for (const item of itens) {
              const adicionaisFormatados = item.adicionais.filter(adicional => adicional.b_adicional === 1);
              const opcionaisFormatados = [...item.adicionais.filter(adicional => adicional.b_adicional === 0), ...item.opcionais];
              
              const produtoData = {
                produto_id: item.produto.id,
                quantidade: item.quantidade,
                valor_unitario: item.valorTotal,
                variacoes: item.variacoes,
                adicionais: adicionaisFormatados,
                opcionais: opcionaisFormatados,
                observacoes: item.complemento || ''
              };
              
              const resultado = await adicionarProduto(brand, comandaAberta.id, produtoData);
              
              // Verificar se houve erro
              if (resultado && (resultado.error || resultado.success === false)) {
                const errorMsg = (resultado.error || resultado.message || '').toLowerCase();
                
                // Se erro de comanda fechada, lançar exceção para criar nova
                if (errorMsg.includes('disponível') || errorMsg.includes('fechada') || errorMsg.includes('não está')) {
                  throw new Error(resultado.error || resultado.message);
                }
                
                throw new Error(resultado.error || resultado.message || 'Erro ao adicionar produto');
              }
            }
            
            setComanda(comandaAberta);
            setUltimaComanda(comandaAberta);
          } catch (error) {
            console.log('[CarrinhoOffcanvas] Erro ao adicionar produto:', error);
            // Se erro de comanda fechada/indisponível, limpar e criar nova
            const errorMsg = (error.response?.data?.error || error.message || error.error || '').toLowerCase();
            console.log('[CarrinhoOffcanvas] Mensagem de erro:', errorMsg);
            
            if (errorMsg.includes('disponível') || errorMsg.includes('fechada') || errorMsg.includes('não está')) {
              localStorage.removeItem('sitri_ultima_comanda');
              setUltimaComanda(null);
              
              // Criar nova comanda
              const dadosComanda = {
                tipo_entrega: 'local',
                b_delivery: false,
                b_takeaway: false,
                b_balcao: false,
                endereco: null,
                mesa: tipoEntrega === 'local' ? (mesa || null) : null,
                nome_cliente: nomeCliente || null,
                mesa_cliente: mesaCliente || null,
                status: 'producao',
                itens: itens.map(item => {
                  const adicionaisFormatados = item.adicionais.filter(adicional => adicional.b_adicional === 1);
                  const opcionaisFormatados = [...item.adicionais.filter(adicional => adicional.b_adicional === 0), ...item.opcionais];
                  return {
                    produto_id: item.produto.id,
                    quantidade: item.quantidade,
                    valor_unitario: item.valorTotal,
                    variacoes: item.variacoes,
                    adicionais: adicionaisFormatados,
                    opcionais: opcionaisFormatados,
                    observacoes: item.complemento || ''
                  };
                })
              };
              
              const comanda = await criarComanda(brand, dadosComanda);
              if (comanda && comanda.id) {
                localStorage.setItem('sitri_ultima_comanda', JSON.stringify(comanda));
                localStorage.setItem('sitri_user_data_updated', 'true');
                
                // Buscar comanda completa do backend
                const { getComanda } = await import('../services/api');
                const comandaCompleta = await getComanda(brand, comanda.id);
                
                setComanda(comandaCompleta || comanda);
                setUltimaComanda(comandaCompleta || comanda);
                
                console.log('[CarrinhoOffcanvas] Nova comanda criada:', comanda.id);
              } else {
                throw new Error('Falha ao criar nova comanda');
              }
            } else {
              throw error;
            }
          }
        } else {
          // Criar nova comanda para mesa
          const dadosComanda = {
            tipo_entrega: 'local',
            b_delivery: false,
            b_takeaway: false,
            b_balcao: false,
            endereco: null,
            mesa: tipoEntrega === 'local' ? (mesa || null) : null, // Só enviar mesa se for tipo local
            nome_cliente: nomeCliente || null,
            mesa_cliente: mesaCliente || null,
            status: 'producao',
            itens: itens.map(item => {
              const adicionaisFormatados = item.adicionais.filter(adicional => adicional.b_adicional === 1);
              const opcionaisFormatados = [...item.adicionais.filter(adicional => adicional.b_adicional === 0), ...item.opcionais];
              
              return {
                produto_id: item.produto.id,
                quantidade: item.quantidade,
                valor_unitario: item.valorTotal,
                valor_total: item.valorTotal * item.quantidade,
                variacoes: item.variacoes,
                adicionais: adicionaisFormatados,
                opcionais: opcionaisFormatados,
                observacoes: item.complemento || '',
                status: 'producao'
              };
            }),
            valor_total: calcularTotal()
          };

          const comanda = await criarComanda(brand, dadosComanda);
          if (comanda && comanda.id) {
            localStorage.setItem('sitri_ultima_comanda', JSON.stringify(comanda));
            localStorage.setItem('sitri_user_data_updated', 'true');
            
            setComanda(comanda);
            setUltimaComanda(comanda);
          } else {
            throw new Error('Falha ao criar comanda');
          }
        }
      }
      
      // Aguardar completar os 2.5 segundos
      setTimeout(() => {
        clearInterval(progressInterval);
        setLoadingProgress(100);
        
        setTimeout(() => {
          setLoading(false);
          setLoadingProgress(0);
          setViewMode('sucesso');
          
          // Disparar evento para recarregar comanda da mesa
          setTimeout(() => {
            window.dispatchEvent(new Event('pedidoFinalizado'));
          }, 500);
          
          // Limpar carrinho após mudar para tela de sucesso
          setTimeout(() => {
            limparCarrinho();
            limparCarrinhoStorage();
            setVoucher('');
            setDescontoVoucher(0);
            setErroVoucher('');
          }, 100);
        }, 300);
      }, 2500);

    } catch (error) {
      clearInterval(progressInterval);
      setLoading(false);
      setLoadingProgress(0);
      
      const errorMessage = error?.message || 'Erro ao enviar pedido. Tente novamente.';
      alert(errorMessage);
      setViewMode('carrinho');
    }
  };



  return (
    <>
      <Offcanvas 
        show={show} 
        onHide={onHide} 
        placement="end" 
        style={{ width: "40%" }}
        className="carrinho-offcanvas"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="d-flex align-items-center">
            {(viewMode === 'pagamento' || viewMode === 'pix') && (
              <Button 
                variant="link" 
                className="p-0 me-2" 
                onClick={() => {
                  if (viewMode === 'pix') {
                    setViewMode('pagamento');
                    setChavePIX('');
                    setPixTimer(360);
                  } else {
                    voltarParaCarrinho();
                  }
                }}
                style={{ textDecoration: 'none' }}
              >
                <ArrowLeft />
              </Button>
            )}
            <h4 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>
              {viewMode === 'carrinho' 
                ? `Seu Pedido (${itens.length} ${itens.length === 1 ? 'item' : 'itens'})` 
                : viewMode === 'pagamento' 
                ? 'Finalizar Pagamento'
                : viewMode === 'pix'
                ? 'Pagamento PIX'
                : viewMode === 'processando'
                ? 'Processando Pagamento'
                : 'Pedido Realizado'
              }
            </h4>
          </Offcanvas.Title>
        </Offcanvas.Header>
        
        <div className="px-3 py-3 border-bottom">
          <div style={{display: 'grid', gridTemplateColumns: '1fr 24px 1fr 24px 1fr', alignItems: 'center'}}>
            <div style={{display:'flex', flexDirection:'column', alignItems:'center', gap:'6px'}}>
              <div style={{
                width:'32px', height:'32px', borderRadius:'50%',
                display:'flex', alignItems:'center', justifyContent:'center',
                backgroundColor: viewMode !== 'carrinho' ? '#10b981' : '#eef2ff',
                color: viewMode !== 'carrinho' ? '#fff' : '#1f2937',
                border: viewMode === 'carrinho' ? '2px solid #0039ff' : 'none',
                fontSize:'12px', fontWeight:600
              }}>
                {viewMode !== 'carrinho' ? <Check /> : '1'}
              </div>
              <div style={{fontSize:'12px', fontWeight:600, color: viewMode === 'carrinho' ? '#1a1a1a' : '#6b7280'}}>Resumo</div>
            </div>
            <div style={{height:'2px', width:'100%', backgroundColor: (viewMode === 'pagamento' || viewMode === 'pix' || viewMode === 'processando' || viewMode === 'sucesso') ? '#10b981' : '#e5e7eb'}}></div>
            <div style={{display:'flex', flexDirection:'column', alignItems:'center', gap:'6px'}}>
              <div style={{
                width:'32px', height:'32px', borderRadius:'50%',
                display:'flex', alignItems:'center', justifyContent:'center',
                backgroundColor: (viewMode === 'processando' || viewMode === 'sucesso') ? '#10b981' : (viewMode === 'pagamento' || viewMode === 'pix') ? '#eef2ff' : '#f3f4f6',
                color: (viewMode === 'processando' || viewMode === 'sucesso') ? '#fff' : '#1f2937',
                border: (viewMode === 'pagamento' || viewMode === 'pix') ? '2px solid #0039ff' : 'none',
                fontSize:'12px', fontWeight:600
              }}>
                {(viewMode === 'processando' || viewMode === 'sucesso') ? <Check /> : '2'}
              </div>
              <div style={{fontSize:'12px', fontWeight:600, color: (viewMode === 'pagamento' || viewMode === 'pix') ? '#1a1a1a' : '#6b7280'}}>Pagamento</div>
            </div>
            <div style={{height:'2px', width:'100%', backgroundColor: (viewMode === 'sucesso') ? '#10b981' : '#e5e7eb'}}></div>
            <div style={{display:'flex', flexDirection:'column', alignItems:'center', gap:'6px'}}>
              <div style={{
                width:'32px', height:'32px', borderRadius:'50%',
                display:'flex', alignItems:'center', justifyContent:'center',
                backgroundColor: viewMode === 'sucesso' ? '#10b981' : (viewMode === 'processando' ? '#eef2ff' : '#f3f4f6'),
                color: viewMode === 'sucesso' ? '#fff' : '#1f2937',
                border: viewMode === 'processando' ? '2px solid #0039ff' : 'none',
                fontSize:'12px', fontWeight:600
              }}>
                {viewMode === 'sucesso' ? <Check /> : '3'}
              </div>
              <div style={{fontSize:'12px', fontWeight:600, color: viewMode === 'sucesso' ? '#1a1a1a' : '#6b7280'}}>Sucesso</div>
            </div>
          </div>
        </div>
        
        <Offcanvas.Body className="bg-light" style={{backgroundColor:'#f3f4f6'}}>
          <div className="container pt-3">
          {viewMode === 'carrinho' ? (
            itens.length === 0 ? (
              <div className="text-center mt-5">
                <Lottie 
                  animationData={deliveryEmptyAnimation} 
                  loop={true}
                  style={{ width: '200px', height: '200px', margin: '0 auto' }}
                />
                <h5>Carrinho vazio</h5>
                <p className="text-muted">Adicione produtos ao seu carrinho</p>
              </div>
            ) : (
              <>
                <h6 className="mb-2" style={{fontSize:'0.95rem', fontWeight:700, color:'#1a1a1a'}}>Resumo do Pedido</h6>
              
                <div className="p-3 mb-4" style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', boxShadow:'0 1px 2px rgba(0,0,0,0.04)'}}>
                  {itens.map((item, index) => (
                    <div key={index} className="d-flex align-items-center py-3" style={{borderBottom: index !== itens.length-1 ? '1px solid #e5e7eb' : 'none'}}>
                      <img 
                        src={item.produto.imagem ? `https://grubupload.s3.amazonaws.com/${item.produto.imagem}` : 'https://grubicons.s3.us-east-1.amazonaws.com/foto-padrao-grub-digital.jpg'}
                        alt={item.produto.nome}
                        style={{width:'50px', height:'50px', objectFit:'cover', borderRadius:'10px', marginRight:'12px'}}
                        onError={(e) => { e.target.src = 'https://grubicons.s3.us-east-1.amazonaws.com/foto-padrao-grub-digital.jpg'; }}
                      />
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between align-items-start mb-1">
                          <div className="fw-bold" style={{fontSize:'0.85rem', color:'#111827'}}>{item.produto.nome}</div>
                          
                        </div>
                        {item.variacoes.length > 0 && (
                          <div style={{color:'#9ca3af', fontSize:'0.7rem'}}>{item.variacoes.map(v => v.nome).join(', ')}</div>
                        )}
                        {(item.adicionais?.length > 0 || item.opcionais?.length > 0 || item.complemento) && (
                          <OverlayTrigger
                            placement="top"
                            overlay={
                              <Tooltip id={`info-item-${index}`}>
                                <div style={{textAlign:'left'}}>
                                  {item.adicionais?.length > 0 && (
                                    <div><strong>Adicionais:</strong> {formatGroup(item.adicionais, true)}</div>
                                  )}
                                  {item.opcionais?.length > 0 && (
                                    <div><strong>Opcionais:</strong> {formatGroup(item.opcionais, false)}</div>
                                  )}
                                  {item.complemento && (
                                    <div><strong>Observação:</strong> {item.complemento}</div>
                                  )}
                                </div>
                              </Tooltip>
                            }
                          >
                            <span className="badge" style={{background:'#eef2ff', color:'#1f2937', border:'1px solid #c7d2fe', fontSize:'0.7rem'}}>+ info do pedido</span>
                          </OverlayTrigger>
                        )}
                      </div>
                      <div className="d-flex align-items-center" style={{gap:'10px'}}>
                        <button onClick={() => alterarQuantidade(index, item.quantidade - 1)} style={{width:'28px',height:'28px',borderRadius:'8px',border:'1px solid #e5e7eb',background:'#fff',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1rem',color:'#000'}}>−</button>
                        <span className="fw-bold" style={{minWidth:'18px', textAlign:'center', fontSize:'0.9rem'}}>{item.quantidade}</span>
                        <button onClick={() => alterarQuantidade(index, item.quantidade + 1)} style={{width:'28px',height:'28px',borderRadius:'8px',border:'none',background:'#111827',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1rem',color:'#fff'}}>+</button>
                      </div>
                      <div className="fw-bold ms-3" style={{fontSize:'0.9rem', color:'#111827'}}>{formatPrice(item.valorTotal * item.quantidade)}</div>
                    </div>
                  ))}
                </div>

                <div className="mb-3">
                  <h6 className="mb-2" style={{fontSize:'0.95rem', fontWeight:700, color:'#1a1a1a'}}>Entrega</h6>
                  <div className="p-3" style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', boxShadow:'0 1px 2px rgba(0,0,0,0.04)'}}>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center" style={{gap:'12px'}}>
                        <div style={{width:'40px', height:'40px', borderRadius:'8px', background:'#fff', display:'flex', alignItems:'center', justifyContent:'center', border:'1px solid #e5e7eb'}}>
                          <Truck style={{color:'#6b7280'}} />
                        </div>
                        <div>
                          <div className="fw-bold" style={{fontSize:'0.9rem', color:'#111827'}}>
                            {tipoEntrega === 'delivery' ? 'Entrega Padrão' : tipoEntrega === 'local' ? 'Mesa' : 'Balcão'}
                          </div>
                          <div className="text-muted" style={{fontSize:'0.8rem'}}>
                            {tipoEntrega === 'delivery' 
                              ? (endereco?.endereco || 'Delivery') 
                              : tipoEntrega === 'local'
                                ? `Mesa ${mesa?.numero || mesa?.identificador || ''}`
                                : (nomeCliente || mesaCliente ? `${nomeCliente || ''}${mesaCliente ? ` - Mesa ${mesaCliente}` : ''}` : 'Balcão')
                            }
                          </div>
                        </div>
                      </div>
                      <div className="text-end d-flex align-items-center" style={{gap:'12px'}}>
                        <div className="fw-bold" style={{fontSize:'0.95rem'}}>{tipoEntrega === 'delivery' ? (taxaEntrega > 0 ? formatPrice(taxaEntrega) : '-') : ''}</div>
                        {tipoEntrega === 'balcao' && (
                          <button
                            onClick={() => setShowBalcaoModal(true)}
                            style={{
                              backgroundColor: '#fff',
                              color: '#6f6f6fff',
                              border: '1px solid #6f6f6fff',
                              borderRadius: '10px',
                              padding: '7px 14px',
                              fontSize: '0.8rem',
                              fontWeight: '600',
                              cursor: 'pointer'
                            }}
                          >
                            Trocar
                          </button>
                        )}
                      </div>
                    </div>
                    {tipoEntrega === 'delivery' && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        {endereco?.entrega?.podeEntregar ? (
                          <div style={{color:'#10b981', fontWeight:'600', fontSize:'0.85rem'}}>
                            Taxa de entrega: {formatPrice(taxaEntrega || 0)}
                          </div>
                        ) : (
                          <div style={{color:'#ef4444', fontWeight:'600', fontSize:'0.85rem'}}>
                            ⚠️ Endereço fora da área de entrega
                          </div>
                        )}
                        <button
                          onClick={() => setShowEnderecoModal(true)}
                          style={{
                            backgroundColor: '#fff',
                            color: '#6f6f6fff',
                            border: '1px solid #6f6f6fff',
                            borderRadius: '10px',
                            padding: '7px 14px',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          {endereco?.entrega?.podeEntregar ? 'Alterar' : 'Alterar'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

              {/* Voucher */}
              {tipoEntrega !== 'local' && (
                <>
                  <div className="mb-3 p-3 d-flex align-items-center justify-content-between" style={{backgroundColor: 'rgb(227, 241, 255)', borderRadius: '16px'}}>
                    <div className="d-flex align-items-center flex-grow-1" style={{marginRight: '12px'}}>
                      <span style={{marginRight: '10px', color: 'rgb(107, 114, 128)'}}>
                        {/* Iconoir substitute for voucher */}
                        <BadgeCheck />
                      </span>
                      <input
                        type="text"
                        placeholder="Inserir cupom de desconto"
                        value={voucher}
                        onChange={(e) => setVoucher(e.target.value)}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          outline: 'none',
                          fontSize: '0.85rem',
                          width: '100%',
                          color: 'rgb(0, 0, 0)'
                        }}
                      />
                    </div>
                    <button
                      onClick={aplicarVoucher}
                      disabled={loading}
                      style={{
                        backgroundColor: '#0039ff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        padding: '8px 20px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      {loading ? 'Validando...' : (
                        <>
                          
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 12h14M12 5l7 7-7 7"/>
                          </svg>
                        </>
                      )}
                    </button>
                  </div>
                  {descontoVoucher > 0 && (
                    <div className="mb-3 text-center" style={{color: '#898989ff', fontSize: '0.8rem'}}>
                      ✓ Desconto aplicado por voucher: <b> {formatPrice(descontoVoucher)}</b>
                    </div>
                  )}
                  {erroVoucher && (
                    <div className="mb-3" style={{color: '#ef4444', fontSize: '0.8rem', paddingLeft: '12px'}}>
                      ✗ {erroVoucher}
                    </div>
                  )}
                </>
              )}







              {/* Resumo de Valores */}
                <div className="mb-3">
                  <h6 className="mb-2" style={{fontSize:'0.95rem', fontWeight:700, color:'#1a1a1a'}}>Resumo de Valores</h6>
                <div className="p-3" style={{background:'#fff', border:'1px solid #e5e7eb', borderRadius:'12px', boxShadow:'0 1px 2px rgba(0,0,0,0.04)'}}>
                  <div className="d-flex justify-content-between mb-3" style={{color: '#9ca3af', fontSize: '0.85rem'}}>
                    <span>Subtotal</span>
                    <span className="fw-bold" style={{color: '#000'}}>{formatPrice(total)}</span>
                  </div>
                  
                  {tipoEntrega === 'delivery' && (
                    <div className="d-flex justify-content-between mb-3" style={{color: '#9ca3af', fontSize: '0.85rem'}}>
                      <span>Taxa da entrega</span>
                      <span className="fw-bold" style={{color: '#000'}}>{formatPrice(taxaEntrega)}</span>
                    </div>
                  )}
                  
                  {taxaServico && tipoEntrega !== 'delivery' && (
                    <div className="d-flex justify-content-between mb-3" style={{color: '#9ca3af', fontSize: '0.85rem'}}>
                      <span>Taxa de serviço (10%)</span>
                      <span className="fw-bold" style={{color: '#000'}}>{formatPrice(valorTaxaServico)}</span>
                    </div>
                  )}
                  
                  {descontoVoucher > 0 && (
                    <div className="d-flex justify-content-between mb-3" style={{fontSize: '0.85rem'}}>
                      <span className="d-flex align-items-center" style={{gap:'8px', color:'#0077ffff'}}>
                        <BadgeCheck />
                        <span>Desconto</span>
                        {voucher && (
                          <span style={{background:'#eaf2ff', color:'#0077ffff', border:'1px solid #cfe0ff', borderRadius:'9999px', padding:'2px 8px', fontSize:'0.75rem'}}>{voucher}</span>
                        )}
                      </span>
                      <span className="fw-bold" style={{color:'#0077ffff'}}>-{formatPrice(descontoVoucher)}</span>
                    </div>
                  )}
                  
                  <div className="d-flex justify-content-between mb-3">
                    <span className="fw-bold" style={{fontSize: '0.95rem'}}>Total</span>
                    <span className="fw-bold" style={{fontSize: '0.95rem', color: '#10b981'}}>{formatPrice(calcularTotal())}</span>
                  </div>
                  
                  <div className="d-grid gap-2">
                    {tipoEntrega !== 'local' && (
                      <Button
                        style={{
                          backgroundColor: podeFinalizarPedido() ? '#0039ff' : '#9ca3af',
                          border: 'none',
                          borderRadius: '12px',
                          padding: '12px',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          boxShadow: podeFinalizarPedido() ? '0 4px 12px rgba(0,57,255,0.3)' : 'none',
                          transition: 'all 0.3s ease',
                          transform: 'translateY(0)'
                        }}
                        onClick={pagarAgora}
                        disabled={!podeFinalizarPedido() || loading}
                        onMouseEnter={(e) => {
                          if (podeFinalizarPedido()) {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,57,255,0.4)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (podeFinalizarPedido()) {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,57,255,0.3)';
                          }
                        }}
                      >
                        {tipoEntrega === 'delivery' && endereco?.entrega && !endereco.entrega.podeEntregar 
                          ? 'Selecione um endereço válido' 
                          : 'Ir para o pagamento'
                        }
                      </Button>
                    )}
                    
                    {(tipoEntrega === 'local' && mesa) && (
                      <Button
                        variant="primary"
                        onClick={pagarComGarcom}
                        disabled={!podeFinalizarPedido() || loading}
                        style={{
                          position: 'relative',
                          backgroundColor: '#0039ff',
                          border: 'none',
                          borderRadius: '12px',
                          padding: '12px',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          boxShadow: '0 4px 12px rgba(0,57,255,0.3)',
                          transition: 'all 0.3s ease',
                          transform: 'translateY(0)',
                          overflow: 'hidden'
                        }}
                        onMouseEnter={(e) => {
                          if (!loading) {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,57,255,0.4)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!loading) {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,57,255,0.3)';
                          }
                        }}
                      >
                        {loading && (
                          <div style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            height: '100%',
                            width: `${loadingProgress}%`,
                            backgroundColor: 'rgba(5, 20, 136, 0.83)',
                            transition: 'width 0.15s ease-out',
                            borderRadius: '12px'
                          }} />
                        )}
                        <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <img src="https://grubicons.s3.us-east-1.amazonaws.com/isologo-grub-branco.svg" alt="GRUB DIGITAL" style={{width: '20px', height: '20px', marginRight: '8px'}} />
                          {loading ? 'Enviando...' : 'Enviar pedido'}
                        </div>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </>
            )
          ) : viewMode === 'pagamento' ? (
            /* Conteúdo de Pagamento */
            <>
              {/* Resumo do Pedido */}
              <div className="mb-3 p-3" style={{backgroundColor: '#f8f9fa', borderRadius: '16px'}}>
                <h6 className="mb-3 fw-bold" style={{fontSize: '0.85rem'}}>Resumo do Pedido</h6>
                {itens.slice(0, 3).map((item, index) => (
                  <div key={index} className="d-flex align-items-center mb-2 pb-2" style={{borderBottom: '1px solid #e5e7eb', gap: '12px'}}>
                    <img 
                      src={item.produto.imagem ? `https://grubupload.s3.amazonaws.com/${item.produto.imagem}` : 'https://grubicons.s3.us-east-1.amazonaws.com/foto-padrao-grub-digital.jpg'}
                      alt={item.produto.nome}
                      style={{ 
                        width: '50px', 
                        height: '50px', 
                        objectFit: 'cover', 
                        borderRadius: '10px',
                        backgroundColor: '#fff',
                        flexShrink: 0
                      }}
                      onError={(e) => {
                        e.target.src = 'https://grubicons.s3.us-east-1.amazonaws.com/foto-padrao-grub-digital.jpg';
                      }}
                    />
                    <div className="flex-grow-1">
                      <div className="fw-bold" style={{fontSize: '0.8rem'}}>{item.produto.nome}</div>
                      <div style={{color: '#9ca3af', fontSize: '0.7rem'}}>Qtd: {item.quantidade}</div>
                      {(item.adicionais?.length > 0 || item.opcionais?.length > 0 || item.complemento) && (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip id={`info-pag-item-${index}`}>
                              <div style={{textAlign:'left'}}>
                                {item.adicionais?.length > 0 && (
                                  <div><strong>Adicionais:</strong> {formatGroup(item.adicionais, true)}</div>
                                )}
                                {item.opcionais?.length > 0 && (
                                  <div><strong>Opcionais:</strong> {formatGroup(item.opcionais, false)}</div>
                                )}
                                {item.complemento && (
                                  <div><strong>Observação:</strong> {item.complemento}</div>
                                )}
                              </div>
                            </Tooltip>
                          }
                        >
                          <span className="badge" style={{background:'#eef2ff', color:'#1f2937', border:'1px solid #c7d2fe', fontSize:'0.7rem'}}>+ info do pedido</span>
                        </OverlayTrigger>
                      )}
                    </div>
                    <div className="text-end" style={{flexShrink: 0}}>
                      <div className="fw-bold" style={{fontSize: '0.8rem'}}>{formatPrice(item.valorTotal * item.quantidade)}</div>
                    </div>
                  </div>
                ))}
                
                {itens.length > 3 && (
                  <div style={{color: '#9ca3af', fontSize: '0.75rem', textAlign: 'center', marginBottom: '8px'}}>
                    ... e mais {itens.length - 3} {itens.length - 3 === 1 ? 'item' : 'itens'}
                  </div>
                )}
                
                <div className="mt-3 pt-3" style={{borderTop: '1px solid #e5e7eb'}}>
                  <div className="d-flex justify-content-between mb-2" style={{color: '#9ca3af', fontSize: '0.8rem'}}>
                    <span>Subtotal</span>
                    <span className="fw-bold" style={{color: '#000'}}>{formatPrice(total)}</span>
                  </div>
                  
                  {tipoEntrega === 'delivery' && (
                    <div className="d-flex justify-content-between mb-2" style={{color: '#9ca3af', fontSize: '0.8rem'}}>
                      <span>Taxa de entrega</span>
                      <span className="fw-bold" style={{color: '#000'}}>{formatPrice(taxaEntrega)}</span>
                    </div>
                  )}
                  
                  {taxaServico && tipoEntrega !== 'delivery' && (
                    <div className="d-flex justify-content-between mb-2" style={{color: '#9ca3af', fontSize: '0.8rem'}}>
                      <span>Taxa de serviço (10%)</span>
                      <span className="fw-bold" style={{color: '#000'}}>{formatPrice(valorTaxaServico)}</span>
                    </div>
                  )}
                  
                  {descontoVoucher > 0 && (
                    <div className="d-flex justify-content-between mb-2" style={{fontSize: '0.8rem'}}>
                      <span className="d-flex align-items-center" style={{gap:'8px', color:'#057affff'}}>
                        <BadgeCheck />
                        <span>Voucher aplicado</span>
                        {voucher && (
                          <span style={{background:'#eaf2ff', color:'#1d4ed8', border:'1px solid #cfe0ff', borderRadius:'9999px', padding:'2px 8px', fontSize:'0.7rem'}}>{voucher}</span>
                        )}
                      </span>
                      <span className="fw-bold" style={{color:'#0039ff'}}>-{formatPrice(descontoVoucher)}</span>
                    </div>
                  )}
                  
                  <div className="d-flex justify-content-between mt-3 pt-2" style={{borderTop: '1px solid #e5e7eb'}}>
                    <span className="fw-bold" style={{fontSize: '0.9rem'}}>Total</span>
                    <span className="fw-bold" style={{fontSize: '0.9rem', color: '#03a46f'}}>{formatPrice(calcularTotal())}</span>
                  </div>
                </div>
              </div>

              {/* Mensagem de Erro */}
              {erroPagamento && (
                <div className="mb-3 p-3" style={{backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px'}}>
                  <div style={{color: '#dc2626', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '8px'}}>
                    <Alarm />
                    {erroPagamento}
                  </div>
                </div>
              )}

             
              
              {/* Formas de Pagamento */}
              <div className="mb-3 p-3" style={{backgroundColor: '#f8f9fa', borderRadius: '16px'}}>
                <h6 className="mb-3 fw-bold" style={{fontSize: '0.9rem'}}>Forma de Pagamento</h6>
                <div style={{display: 'grid', gridTemplateColumns: (tipoEntrega === 'balcao' && (window.lojaInfo?.configuracoes?.b_pedido_sem_pagamento === 1 || window.lojaInfo?.configuracoes?.b_pedido_sem_pagamento === true)) ? '1fr 1fr 1fr' : '1fr 1fr', gap: '12px', marginBottom: '16px'}}>
                  <button
                    onClick={() => {
                      setTipoPagamento('PIX');
                    }}
                    style={{
                      backgroundColor: '#fff',
                      border: tipoPagamento === 'PIX' ? '2px solid #0039ff' : '1px solid #e5e7eb',
                      borderRadius: '12px',
                      padding: '16px 10px',
                      cursor: 'pointer',
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      minHeight: '90px'
                    }}
                  >
                    {tipoPagamento === 'PIX' && (
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: '#0039ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <div style={{width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#fff'}}></div>
                      </div>
                    )}
                    <img 
                      src="https://grubicons.s3.us-east-1.amazonaws.com/pix.svg" 
                      alt="PIX" 
                      style={{width: '28px', height: '28px'}}
                    />
                    <span style={{fontSize: '0.85rem', fontWeight: '600', color: '#000', lineHeight: '1'}}>PIX</span>
                  </button>
                  <button
                    onClick={() => {
                      setTipoPagamento('CREDITO');
                    }}
                    style={{
                      backgroundColor: '#fff',
                      border: tipoPagamento === 'CREDITO' ? '2px solid #0039ff' : '1px solid #e5e7eb',
                      borderRadius: '12px',
                      padding: '16px 10px',
                      cursor: 'pointer',
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      minHeight: '90px'
                    }}
                  >
                    {tipoPagamento === 'CREDITO' && (
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: '#0039ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <div style={{width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#fff'}}></div>
                      </div>
                    )}
                    <div style={{position: 'relative', width: '40px', height: '40px'}}>
                      <Lottie 
                        animationData={cartaoCreditoAnimation} 
                        loop={true}
                        style={{ width: '100%', height: '100%' }}
                      />
                    </div>
                    <span style={{fontSize: '0.85rem', fontWeight: '600', color: '#000'}}>Crédito</span>
                  </button>
                  {tipoEntrega === 'balcao' && (window.lojaInfo?.configuracoes?.b_pedido_sem_pagamento === 1 || window.lojaInfo?.configuracoes?.b_pedido_sem_pagamento === true) && (
                    <button
                      onClick={() => {
                        setTipoPagamento('GARCOM');
                      }}
                      style={{
                        backgroundColor: '#fff',
                        border: tipoPagamento === 'GARCOM' ? '2px solid #0039ff' : '1px solid #e5e7eb',
                        borderRadius: '12px',
                        padding: '16px 10px',
                        cursor: 'pointer',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        minHeight: '90px'
                      }}
                    >
                      {tipoPagamento === 'GARCOM' && (
                        <div style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: '#0039ff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <div style={{width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#fff'}}></div>
                        </div>
                      )}
                      <div style={{position: 'relative', width: '40px', height: '40px'}}>
                        <Lottie 
                          animationData={posCartaoAnimation} 
                          loop={true}
                          style={{ width: '100%', height: '100%' }}
                        />
                      </div>
                      <span style={{fontSize: '0.85rem', fontWeight: '600', color: '#000'}}>Garçom</span>
                    </button>
                  )}
                </div>

                {tipoPagamento === 'PIX' && (
                  <div>
                    {!chavePIX ? (
                      <div>
                        <div style={{backgroundColor: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #e5e7eb', marginBottom: '16px'}}>
                          <h6 style={{fontSize: '0.85rem', fontWeight: '600', marginBottom: '12px'}}>Dados para PIX</h6>
                          <div style={{marginBottom: '12px'}}>
                            <input
                              type="text"
                              placeholder="Nome do pagador"
                              value={cardForm.name}
                              onChange={(e) => handleCardChange('name', e.target.value.toUpperCase())}
                              style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                fontSize: '0.85rem',
                                marginBottom: '12px'
                              }}
                            />
                            <input
                              type="text"
                              inputMode="numeric"
                              placeholder="CPF"
                              value={cardForm.cpf}
                              onChange={(e) => handleCardChange('cpf', formatCPF(e.target.value))}
                              maxLength={14}
                              style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                fontSize: '0.85rem'
                              }}
                            />
                          </div>
                          <p style={{color: '#9ca3af', fontSize: '0.75rem', marginBottom: '12px'}}>Obrigatório para identificação no PIX</p>
                          <button
                            onClick={gerarPIX}
                            disabled={loading || !cardForm.cpf || cardForm.cpf.replace(/\D/g, '').length !== 11 || !cardForm.name}
                            style={{
                              position: 'relative',
                              backgroundColor: '#4fbeb0',
                              color: 'white',
                              border: 'none',
                              borderRadius: '10px',
                              padding: '14px',
                              fontSize: '0.85rem',
                              fontWeight: '600',
                              cursor: loading ? 'not-allowed' : 'pointer',
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '8px',
                              opacity: (!cardForm.cpf || cardForm.cpf.replace(/\D/g, '').length !== 11 || !cardForm.name) ? 0.5 : 1,
                              overflow: 'hidden',
                              boxShadow: '0 4px 12px rgba(79, 190, 176, 0.3)',
                              transition: 'all 0.3s ease',
                              transform: 'translateY(0)'
                            }}
                            onMouseEnter={(e) => {
                              if (!loading && cardForm.cpf && cardForm.cpf.replace(/\D/g, '').length === 11 && cardForm.name) {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 6px 16px rgba(79, 190, 176, 0.4)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!loading && cardForm.cpf && cardForm.cpf.replace(/\D/g, '').length === 11 && cardForm.name) {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(79, 190, 176, 0.3)';
                              }
                            }}
                          >
                            {loading && (
                              <div style={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                height: '100%',
                                width: `${loadingProgress}%`,
                                backgroundColor: 'rgba(32, 150, 138, 0.83)',
                                transition: 'width 0.15s ease-out',
                                borderRadius: '10px'
                              }} />
                            )}
                            <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                              <img src="https://grubicons.s3.us-east-1.amazonaws.com/pix.svg" alt="PIX" style={{width: '18px', height: '18px', filter: 'brightness(0) invert(1)'}} />
                              {loading ? 'Gerando...' : 'Gerar Código PIX'}
                            </div>
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center">
                        <p style={{fontSize: '0.8rem', marginBottom: '12px'}}>Copie o código abaixo e cole no seu banco:</p>
                        <div style={{backgroundColor: '#fff', padding: '12px', borderRadius: '10px', marginBottom: '12px', fontSize: '0.7rem', wordBreak: 'break-all', maxHeight: '100px', overflowY: 'auto', border: '1px solid #e5e7eb'}}>
                          {chavePIX}
                        </div>
                        <button
                          onClick={copiarPIX}
                          style={{
                            backgroundColor: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '10px',
                            padding: '10px',
                            fontSize: '0.85rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                        >
                          <Copy />
                          Copiar Código PIX
                        </button>
                        <p style={{color: '#9ca3af', fontSize: '0.75rem', marginTop: '12px', marginBottom: '0'}}>
                          Após o pagamento, seu pedido será enviado automaticamente.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {tipoPagamento === 'CREDITO' && usuario && cartaoSelecionado && (() => {
                  const detectedBrand = (cartaoSelecionado.bandeira && cartaoSelecionado.bandeira !== 'UNKNOWN') ? cartaoSelecionado.bandeira : 'UNKNOWN';
                  const brandLogo = getCardBrandLogo(detectedBrand);
                  const brandName = getCardBrandName(detectedBrand);
                  
                  return (
                  <div style={{backgroundColor: '#fff', padding: '16px', borderRadius: '12px', border: '2px solid #3c77ff', marginBottom: '16px'}}>
                    <div className="d-flex justify-content-between align-items-center">
                      <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                        <div style={{width:'40px', height:'40px', display:'flex', alignItems:'center', justifyContent:'center'}}>
                          {brandLogo ? (
                            <img src={brandLogo} alt={brandName} style={{width:'32px', height:'auto'}} />
                          ) : (
                            <CreditCard style={{fontSize: '1.5rem', color: '#3c77ff'}} />
                          )}
                        </div>
                        <div>
                          <div style={{fontSize: '0.85rem', fontWeight: '600', color: '#000'}}>{brandName} **** {cartaoSelecionado.numero}</div>
                        </div>
                      </div>
                      <div style={{display: 'flex', gap: '8px'}}>
                        <button
                          onClick={() => setShowCartoesSalvosModal(true)}
                          style={{
                            backgroundColor: '#fff',
                            color: '#0039ff',
                            border: '1px solid #0039ff',
                            borderRadius: '8px',
                            padding: '6px 16px',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          Trocar
                        </button>
                        <button
                          onClick={() => {
                            setCartaoSelecionado(null);
                            setUsarNovoCartao(true);
                          }}
                          style={{
                            backgroundColor: '#fff',
                            color: '#6b7280',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            padding: '6px 16px',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            cursor: 'pointer'
                          }}
                        >
                          Novo
                        </button>
                      </div>
                    </div>
                  </div>
                  );
                })()}

                {tipoPagamento === 'CREDITO' && (
                  <div style={{marginBottom: '16px'}}>
                    {/* Formulário de cartão para balcão ou quando quer usar novo cartão */}
                    {(usarNovoCartao || tipoEntrega === 'balcao' || !cartaoSelecionado) && (
                      <div style={{backgroundColor: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #e5e7eb'}}>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 style={{fontSize: '0.85rem', fontWeight: '600', margin: 0}}>Dados do Cartão</h6>
                          {usarNovoCartao && tipoEntrega !== 'balcao' && (
                            <button
                              onClick={() => setUsarNovoCartao(false)}
                              style={{
                                backgroundColor: '#fff',
                                color: '#ef4444',
                                border: '1px solid #ef4444',
                                borderRadius: '8px',
                                padding: '4px 12px',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                cursor: 'pointer'
                              }}
                            >
                              Cancelar
                            </button>
                          )}
                        </div>
                        
                        <div style={{marginBottom: '12px'}}>
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder="Número do cartão"
                            value={cardForm.number}
                            onChange={(e) => handleCardChange('number', formatCardNumber(e.target.value))}
                            maxLength={19}
                            style={{
                              width: '100%',
                              padding: '10px',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              fontSize: '0.85rem'
                            }}
                          />
                        </div>
                        
                        <div style={{marginBottom: '12px'}}>
                          <input
                            type="text"
                            placeholder="Nome do titular"
                            value={cardForm.name}
                            onChange={(e) => handleCardChange('name', e.target.value.toUpperCase())}
                            style={{
                              width: '100%',
                              padding: '10px',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              fontSize: '0.85rem'
                            }}
                          />
                        </div>
                        
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px'}}>
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder="CVV"
                            value={cardForm.cvc}
                            onChange={(e) => handleCardChange('cvc', e.target.value.replace(/\D/g, ''))}
                            maxLength={3}
                            style={{
                              padding: '10px 8px',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              fontSize: '0.85rem',
                              width: '100%',
                              boxSizing: 'border-box'
                            }}
                          />
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder="MM/AA"
                            value={cardForm.expiry}
                            onChange={(e) => handleCardChange('expiry', formatExpiry(e.target.value))}
                            maxLength={5}
                            style={{
                              padding: '10px 8px',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              fontSize: '0.85rem',
                              width: '100%',
                              boxSizing: 'border-box'
                            }}
                          />
                        </div>
                        
                        <div style={{marginBottom: '12px'}}>
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder="CPF do titular"
                            value={cardForm.cpf}
                            onChange={(e) => handleCardChange('cpf', formatCPF(e.target.value))}
                            maxLength={14}
                            style={{
                              width: '100%',
                              padding: '10px',
                              border: '1px solid #e5e7eb',
                              borderRadius: '8px',
                              fontSize: '0.85rem'
                            }}
                          />
                        </div>
                        
                        <button
                          onClick={pagarComCartao}
                          disabled={!cardForm.number || !cardForm.name || !cardForm.cvc || !cardForm.expiry || !cardForm.cpf || cardForm.cpf.replace(/\D/g, '').length !== 11}
                          style={{
                            backgroundColor: (!cardForm.number || !cardForm.name || !cardForm.cvc || !cardForm.expiry || !cardForm.cpf || cardForm.cpf.replace(/\D/g, '').length !== 11) ? '#9ca3af' : '#0039ff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '12px',
                            padding: '14px',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            cursor: (!cardForm.number || !cardForm.name || !cardForm.cvc || !cardForm.expiry || !cardForm.cpf || cardForm.cpf.replace(/\D/g, '').length !== 11) ? 'not-allowed' : 'pointer',
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            boxShadow: (!cardForm.number || !cardForm.name || !cardForm.cvc || !cardForm.expiry || !cardForm.cpf || cardForm.cpf.replace(/\D/g, '').length !== 11) ? 'none' : '0 4px 12px rgba(0,119,255,0.3)',
                            transition: 'all 0.3s ease',
                            transform: 'translateY(0)',
                            opacity: (!cardForm.number || !cardForm.name || !cardForm.cvc || !cardForm.expiry || !cardForm.cpf || cardForm.cpf.replace(/\D/g, '').length !== 11) ? 0.6 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (cardForm.number && cardForm.name && cardForm.cvc && cardForm.expiry && cardForm.cpf && cardForm.cpf.replace(/\D/g, '').length === 11) {
                              e.target.style.transform = 'translateY(-2px)';
                              e.target.style.boxShadow = '0 6px 16px rgba(0,119,255,0.4)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (cardForm.number && cardForm.name && cardForm.cvc && cardForm.expiry && cardForm.cpf && cardForm.cpf.replace(/\D/g, '').length === 11) {
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = '0 4px 12px rgba(0,119,255,0.3)';
                            }
                          }}
                        >
                          <img src="https://grubicons.s3.us-east-1.amazonaws.com/isologo-grub-branco.svg" alt="Grub" style={{width: '20px', height: '20px'}} />
                          Pagar {formatPrice(calcularTotal())}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {tipoPagamento === 'CREDITO' && cartaoSelecionado && tipoEntrega !== 'balcao' && (
                  <button
                    onClick={pagarComCartaoSalvo}
                    style={{
                      backgroundColor: '#0039ff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      padding: '14px',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      marginBottom: '12px',
                      boxShadow: '0 4px 12px rgba(0,119,255,0.3)',
                      transition: 'all 0.3s ease',
                      transform: 'translateY(0)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 6px 16px rgba(0,119,255,0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 4px 12px rgba(0,119,255,0.3)';
                    }}
                  >
                    <img src="https://grubicons.s3.us-east-1.amazonaws.com/isologo-grub-branco.svg" alt="GRUB DIGITAL" style={{width: '20px', height: '20px'}} />
                    Pagar {formatPrice(calcularTotal())}
                  </button>
                )}

                {tipoPagamento === 'GARCOM' && tipoEntrega === 'balcao' && (window.lojaInfo?.configuracoes?.b_pedido_sem_pagamento === 1 || window.lojaInfo?.configuracoes?.b_pedido_sem_pagamento === true) && (
                  <div>
                    <div style={{backgroundColor: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #e5e7eb', marginBottom: '16px'}}>
                      <h6 style={{fontSize: '0.85rem', fontWeight: '600', marginBottom: '12px'}}>Pagar com Garçom</h6>
                      <p style={{color: '#6b7280', fontSize: '0.8rem', marginBottom: '16px'}}>
                        Seu pedido será enviado para a cozinha e você poderá pagar diretamente com o garçom.
                      </p>
                      <button
                        onClick={pagarComGarcom}
                        disabled={loading}
                        style={{
                          position: 'relative',
                          backgroundColor: '#0039ff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '12px',
                          padding: '14px',
                          fontSize: '0.9rem',
                          fontWeight: '600',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          boxShadow: '0 4px 12px rgba(0,57,255,0.3)',
                          transition: 'all 0.3s ease',
                          transform: 'translateY(0)',
                          overflow: 'hidden'
                        }}
                        onMouseEnter={(e) => {
                          if (!loading) {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,57,255,0.4)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!loading) {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,57,255,0.3)';
                          }
                        }}
                      >
                        {loading && (
                          <div style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            height: '100%',
                            width: `${loadingProgress}%`,
                            backgroundColor: 'rgba(5, 20, 136, 0.83)',
                            transition: 'width 0.15s ease-out',
                            borderRadius: '12px'
                          }} />
                        )}
                        <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <img src="https://grubicons.s3.us-east-1.amazonaws.com/isologo-grub-branco.svg" alt="GRUB DIGITAL" style={{width: '20px', height: '20px', marginRight: '8px'}} />
                          {loading ? 'Enviando...' : 'Enviar pedido'}
                        </div>
                      </button>
                    </div>
                  </div>
                )}

                {tipoPagamento && tipoPagamento !== 'GARCOM' && (
                  <div style={{backgroundColor: '#e3f2fd', padding: '20px', marginTop: '30px', borderRadius: '10px', fontSize: '0.8rem', color: '#1976d2', display: 'flex', alignItems: 'center', gap: '8px'}}>
                    <CardShield />
                    {tipoEntrega === 'balcao' ? 'Não armazenamos os dados do seu cartão.' : 'Seus dados estão seguros conosco.'}
                  </div>
                )}
              </div>
            </>
          ) : viewMode === 'pix' ? (
          
            <div className="text-center">
              <div className="mb-4">
                <div style={{
                  width: '30px',
                  height: '30px',
                  borderRadius: '50%',
                  backgroundColor: '#00bfa5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 10px'
                }}>
                  <img 
                    src="https://grubicons.s3.us-east-1.amazonaws.com/pix.svg" 
                    alt="PIX" 
                    style={{width: '13px', height: '13px', filter: 'brightness(0) invert(1)'}}
                  />
                </div>
                <h5 style={{color: '#111827', marginBottom: '8px'}}>Pague com PIX</h5>
                
              </div>

              {/* Timer */}
              <div className="mb-2 p-2 py-2" style={{
                backgroundColor: pixTimer <= 60 ? '#fef2f2' : '#f0f9ff',
                borderRadius: '12px',
                border: `1px solid ${pixTimer <= 60 ? '#fecaca' : '#bfdbfe'}`
              }}>
                <div style={{
                  fontSize: '1.5rem',
                  fontWeight: '700',
                  color: pixTimer <= 60 ? '#dc2626' : '#1d4ed8',
                  marginBottom: '4px'
                }}>
                  {formatTime(pixTimer)}
                </div>
                <div style={{
                  fontSize: '0.8rem',
                  color: pixTimer <= 60 ? '#991b1b' : '#1e40af'
                }}>
                  {pixTimer <= 60 ? 'Código expirando!' : 'Tempo restante para pagamento'}
                </div>
              </div>

              {/* Código PIX */}
              <div className="mb-4 mt-4">
               

                <div style={{ position: 'relative' }}>
                  {showCopiadoAnimation && (
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '300px',
                      height: '300px',
                      pointerEvents: 'none',
                      zIndex: 0
                    }}>
                      <Lottie 
                        animationData={metaAtingidaAnimation} 
                        loop={false}
                        style={{ width: '100%', height: '100%' }}
                      />
                    </div>
                  )}
                  <button 
                    onClick={copiarPIX}
                    data-pix-copy
                    style={{
                      backgroundColor: showCopiadoAnimation ? '#00a28cff' : '#00bfa5',
                      color: 'white',
                      border: 'none',
                      borderRadius: '12px',
                      padding: '14px 24px',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      marginBottom: '16px',
                      transition: 'all 0.3s ease',
                      position: 'relative',
                      zIndex: 1,
                      boxShadow: '0 4px 12px rgba(0, 191, 165, 0.3)',
                      transform: 'translateY(0)'
                    }}
                    onMouseEnter={(e) => {
                      if (!showCopiadoAnimation) {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 191, 165, 0.4)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!showCopiadoAnimation) {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 191, 165, 0.3)';
                      }
                    }}
                  >
                    {showCopiadoAnimation ? (
                      <>
                        <Check />
                        Copiado com sucesso
                      </>
                    ) : (
                      <>
                        <Copy />
                        Copiar Código PIX
                      </>
                    )}
                  </button>
                </div>
                </div>

             <div style={{
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '10px',
                  marginBottom: '10px'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#64748b',
                    marginBottom: '8px',
                    textAlign: 'left'
                  }}>
                    Código PIX:
                  </div>
                  <div style={{
                    fontSize: '0.7rem',
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                    lineHeight: '1.2',
                    color: '#1e293b',
                    backgroundColor: '#ffffff',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    maxHeight: '120px',
                    overflowY: 'auto'
                  }}>
                    {chavePIX}
                  </div>
                </div>

              <div style={{
                fontSize: '0.75rem',
                color: '#9ca3af',
                marginTop: '20px',
                lineHeight: '1.4'
              }}>
                Após o pagamento, seu pedido será confirmado automaticamente.
               Você pode acompanhar o status no menu "Meus Pedidos".
              </div>
            </div>
          ) : viewMode === 'processando' ? (
            /* Tela de Processamento */
            <div className="text-center py-5">
              <div className="mb-4">
                <Lottie 
                  animationData={tipoPagamento === 'GARCOM' ? pedidoEnviadoCozinhaAnimation : segurancaAnimation} 
                  loop={true}
                  style={{ width: tipoPagamento === 'GARCOM' ? '350px' : '350px', height: tipoPagamento === 'GARCOM' ? '200px' : '350px', margin: '0 auto' }}
                />
              </div>
              <h4 style={{color: '#111827', marginBottom: '12px', fontWeight: '600'}}>
                {tipoPagamento === 'GARCOM' ? 'Enviando o pedido para a cozinha' : 'Processando Pagamento'}
              </h4>
              <p style={{color: '#6b7280', fontSize: '0.95rem', marginBottom: '0'}}>
                {tipoPagamento === 'GARCOM' 
                  ? 'Seu pedido foi processado e enviado para a cozinha e será entregue em breve...'
                  : 'Aguarde enquanto confirmamos seu pagamento...'
                }
              </p>
            </div>
          ) : (
            /* Tela de Sucesso */
            <div className="text-center py-5">
              <div className="mb-4">
                <Lottie 
                  animationData={pedidoEnviadoAnimation} 
                  loop={true}
                  style={{ width: '200px', height: '200px', margin: '0 auto' }}
                />
              </div>
              <h4 className="text-success mb-3">Pedido enviado</h4>
              <p className="text-muted mb-4">Você pode acompanhá-lo <br></br>em meus pedidos.</p>
              
             
               <Button 
                variant="outline-primary" 
                onClick={() => {
                  onHide();
                  if (onOpenMeusPedidos) {
                    setTimeout(() => onOpenMeusPedidos(), 300);
                  }
                }}
                style={{
                  borderRadius: '12px',
                  padding: '12px 24px',
                  fontSize: '0.9rem',
                  fontWeight: '600'
                }}
              >
                <Bag className="me-2" />
                IR A MEUS PEDIDOS
              </Button>
             
             
             
              {/* Convite ao Login - apenas se não estiver logado */}
              {!usuario && (
                <div className="mt-5 mb-4 text-center" style={{
                  background: '#eaf2ff',
                  border: '1px solid #cfe0ff',
                  borderRadius: '16px',
                  padding: '20px'
                }}>
                  <div style={{width: '220px', height: '220px', margin: '0 auto'}}>
                    <Lottie 
                      animationData={cashbackAnimation} 
                      loop={true}
                      style={{width: '100%', height: '100%'}}
                    />
                  </div>
                  <div style={{fontSize: '1rem', fontWeight: 700, color: '#0f172a', marginTop: '8px'}}>Ganhe CASHBACK<br />na próxima compra.</div>

              <div style={{color: '#6b7280', fontSize: '0.9rem'}}>No Clube de Benefícios GRUB, <br /> você sempre sai ganhando.</div>
                  <button 
                    className="btn btn-primary mt-4"
                    style={{marginTop: '12px', padding: '10px 16px', borderRadius: '10px', fontWeight: 600, fontSize: '0.85rem'}}
                    onClick={() => setShowLoginModal(true)}
                  >
                    <LogIn className="me-1" />
                    Cadastre-se
                  </button>
                </div>
              )}
              
              {/* Mensagem para usuário logado removida conforme solicitação */}
              
            
            </div>
          )}
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Modal de Endereço */}
      <EnderecoSelectorModal
        show={showEnderecoModal}
        onHide={() => setShowEnderecoModal(false)}
        brand={brand}
      />
      
      {/* Modal de Login */}
      <LoginModal
        show={showLoginModal}
        onHide={() => setShowLoginModal(false)}
        onLogin={handleLoginSuccess}
        dadosPreenchidos={{
          nome: nomeCliente || endereco?.entrega?.nome || '',
          telefone: endereco?.entrega?.telefone || '',
          cpf: cardForm.cpf || ''
        }}
        brandLogoUrl={window.lojaInfo?.logo_url}
      />
      
      {/* Modal de Dados do Usuário */}
      <UserDataModal
        show={showUserDataModal}
        onHide={() => {}} // Não permitir fechar sem preencher
        onSave={handleSaveUserData}
        loading={loadingUserData}
        initialData={{
          nome: usuario?.nome || '',
          email: usuario?.email || '',
          telefone: usuario?.telefone || ''
        }}
      />

      {/* Modal de Cartões Salvos */}
      <CartoesSalvosModal
        show={showCartoesSalvosModal}
        onHide={() => {
          setShowCartoesSalvosModal(false);
        }}
        onSelectCard={(cartao) => {
          setCartaoSelecionado(cartao);
          setUsarNovoCartao(false);
          setShowCartoesSalvosModal(false);
          // Limpar formulário de cartão novo
          setCardForm({
            number: '',
            name: '',
            cvc: '',
            expiry: '',
            cpf: cardForm.cpf // Manter CPF
          });
        }}
      />

      {/* Modal de Balcão */}
      <Modal show={showBalcaoModal} onHide={() => setShowBalcaoModal(false)} centered>
        <Modal.Header style={{ borderBottom: '1px solid #e5e7eb', padding: '20px' }}>
          <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1a1a1a' }}>
            <Bag className="me-2" style={{ color: '#0039ff' }} />
            Informações do Balcão
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: '24px', paddingBottom: '90px', backgroundColor: '#f9fafb' }}>
          <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '20px' }}>
            Para continuar com seu pedido no balcão, precisamos de algumas informações:
          </p>
          
          <div className="d-flex align-items-center p-3 mb-3" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e5e7eb', color: '#6b7280' }}>
              <User />
            </div>
            <div className="flex-grow-1" style={{ marginLeft: '12px' }}>
              <div className="small text-muted mb-1" style={{ fontSize: '0.75rem', color: '#6b7280' }}>Nome do Cliente</div>
              <Form.Control
                type="text"
                placeholder="Digite como quer ser identificado/a"
                value={nomeCliente}
                onChange={(e) => setNomeCliente(e.target.value)}
                style={{ border: 'none', padding: '4px 0', fontSize: '0.9rem', backgroundColor: 'transparent' }}
              />
            </div>
          </div>
          
          <div className="d-flex align-items-center p-3" style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #e5e7eb', color: '#6b7280' }}>
              <Bag />
            </div>
            <div className="flex-grow-1" style={{ marginLeft: '12px' }}>
              <div className="small text-muted mb-1" style={{ fontSize: '0.75rem', color: '#6b7280' }}>Número da Mesa</div>
              <Form.Control
                type="text"
                placeholder="Digite apenas o número ou descreva o lugar"
                value={mesaCliente}
                onChange={(e) => setMesaCliente(e.target.value)}
                style={{ border: 'none', padding: '4px 0', fontSize: '0.9rem', backgroundColor: 'transparent' }}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer style={{ position: 'fixed', bottom: 0, left: 0, right: 0, borderTop: '1px solid #e5e7eb', padding: '20px', backgroundColor: '#fff', zIndex: 1050 }}>
          <Button 
            variant="primary" 
            onClick={() => setShowBalcaoModal(false)}
            className="w-100"
            style={{
              padding: '12px',
              borderRadius: '12px',
              backgroundColor: '#0039ff',
              border: 'none',
              fontSize: '0.9rem',
              fontWeight: '600',
              boxShadow: '0 4px 12px rgba(0,106,255,0.25)'
            }}
          >
          Continuar com o pedido
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Upsell */}
      <UpselModal
        show={showUpselModal}
        onHide={() => setShowUpselModal(false)}
        products={upselProducts}
        loading={loadingUpsels}
        onSelectProduct={(produto) => {
          // Disparar evento para abrir modal do produto sem fechar o upsell
          const event = new CustomEvent('openProdutoFromUpsell', { 
            detail: { produto }
          });
          window.dispatchEvent(event);
        }}
        onGoToCart={() => {
          setShowUpselModal(false);
        }}
      />

    </>
  );
}

export default CarrinhoOffcanvas;
